import ManualPagina, { Botao, Dica, Atencao, Passo } from '../components/ManualBase'

// Manual do Manifestador de NFS-e Nacional (Compras → Manifestação e Eventos Eletrônicos → Manifestador de NFS-e).
// Texto fixo: se a tela mudar, atualizar aqui.

const SECOES = [
  { id: 'objetivo', rot: '1. Para que serve' },
  { id: 'antes', rot: '2. Antes de começar' },
  { id: 'tela', rot: '3. Conhecendo a tela' },
  { id: 'dia', rot: '4. Passo a passo' },
  { id: 'rejeitar', rot: '5. Como rejeitar uma NFS-e' },
  { id: 'problemas', rot: '6. Mensagens e problemas comuns' },
  { id: 'duvidas', rot: '7. Dúvidas frequentes' },
  { id: 'glossario', rot: '8. Glossário' },
]

export default function ManualManifestadorNFSe() {
  return (
    <ManualPagina titulo="Manual do Manifestador de NFS-e" subtitulo="Como receber, conferir e manifestar as notas de serviço emitidas contra a empresa"
      voltarTexto="Voltar ao manifestador" voltarPara="/compras/manifestacao/nfse" secoes={SECOES}>

      <h2 id="objetivo">1. Para que serve</h2>
      <p>
        Quando uma empresa presta um serviço para a sua e emite a NFS-e (nota fiscal de serviço), a sua empresa é a <b>tomadora</b> do serviço.
        O Manifestador de NFS-e Nacional faz três coisas:
      </p>
      <ol>
        <li><b>Busca</b> no sistema nacional de NFS-e (o ADN) as notas de serviço emitidas contra o CNPJ da empresa.</li>
        <li><b>Permite responder</b> a cada nota: <b>confirmar</b> que o serviço foi prestado ou <b>rejeitar</b> a nota, informando o motivo.</li>
        <li><b>Leva a nota para o Lançamento de Notas</b>, sem precisar digitar tudo de novo.</li>
      </ol>
      <p>Menu: <b>Compras → Manifestação e Eventos Eletrônicos → Manifestador de NFS-e</b> (a tela se chama “Manifestador de NFS-e Nacional”).</p>
      <Dica>Aqui aparecem só as notas de <b>serviços que a empresa recebeu</b> (a empresa é a tomadora). As notas de serviço que a própria empresa emitiu não entram nesta lista.</Dica>
      <Dica>Resumo do dia a dia: <Botao t="Atualizar Lista" /> → conferir a nota → <Botao t="Importar XML pro Lançamento de Notas" /> → <Botao t="Confirmar Operação" />.</Dica>

      <h2 id="antes">2. Antes de começar</h2>
      <ul>
        <li><b>Empresa:</b> a tela trabalha com a empresa escolhida no seletor <b>“Empresa”</b> do topo do sistema. O campo EMPRESA dos filtros só mostra qual é; para trocar, use o seletor do topo.</li>
        <li><b>Certificado digital e CNPJ:</b> a empresa precisa ter o certificado configurado e o CNPJ cadastrado. Sem isso, o sistema avisa “Certificado digital da empresa não configurado” ou “Empresa sem CNPJ cadastrado” e nada é enviado.</li>
        <li><b>Ambiente:</b> o ambiente (teste ou produção) segue a configuração do sistema. Em produção, tudo o que você confirma ou rejeita vale de verdade.</li>
        <li><b>Período:</b> a tela abre mostrando os <b>últimos 7 dias</b>. Se você digitar a chave (ou só o número da nota), o período é ignorado e a nota aparece mesmo sendo antiga.</li>
      </ul>

      <h2 id="tela">3. Conhecendo a tela</h2>

      <h3>Filtros</h3>
      <ul>
        <li><b>Emissão de / até:</b> data de emissão da nota.</li>
        <li><b>Situação:</b> Todas, Confirmada, Rejeitada ou Pendente.</li>
        <li><b>Digitada:</b> Todas, Digitadas (já lançadas no sistema) ou Não digitadas (ainda por lançar).</li>
        <li><b>Participante:</b> o CNPJ do prestador do serviço.</li>
        <li><b>Chave de 50 dígitos ou só o número.</b></li>
        <li><Botao t="Pesquisar" /> aplica os filtros e <Botao t="Limpar" /> volta ao padrão (últimos 7 dias).</li>
      </ul>

      <h3>Colunas da lista</h3>
      <table className="mn-tabela">
        <thead><tr><th>Coluna</th><th>O que mostra</th></tr></thead>
        <tbody>
          <tr><td>Número</td><td>O número da NFS-e. Se a nota já foi lançada, fica azul e sublinhado: clique para abrir na Consulta de Notas. Se o prestador cancelou a nota, aparece uma marca de cancelada.</td></tr>
          <tr><td>Prestador / Nome Prestador</td><td>CNPJ e nome de quem prestou o serviço.</td></tr>
          <tr><td>DPS / Série DPS</td><td>Número e série da DPS, o documento que o prestador envia para gerar a NFS-e.</td></tr>
          <tr><td>Competência</td><td>O mês a que o serviço se refere.</td></tr>
          <tr><td>Finalidade</td><td>NFS-e regular, NFS-e de crédito ou NFS-e de débito.</td></tr>
          <tr><td>Descrição</td><td>O texto do serviço prestado.</td></tr>
          <tr><td>Emissão / Valor R$</td><td>Data de emissão e valor do serviço.</td></tr>
          <tr><td>Confirmada / Rejeitada</td><td>Se você já confirmou ou rejeitou a nota.</td></tr>
          <tr><td>Lançada / Lançada em</td><td>Se a nota já foi lançada no sistema e quando.</td></tr>
        </tbody>
      </table>
      <Atencao>Se o prestador <b>cancelou</b> a nota, ela <b>não pode ser lançada nem manifestada</b>.</Atencao>

      <h3>Resumo e caixa da chave</h3>
      <p>
        Embaixo da lista ficam os totais: <b>Confirmadas</b>, <b>Total Confirmado R$</b>, <b>Pendentes</b> e <b>Total Pendente R$</b>.
        <b> Pendente</b> é a nota que ainda não foi confirmada, nem rejeitada, nem cancelada (o que ainda falta tratar fica em laranja).
      </p>
      <p>
        A tela trabalha com <b>uma nota por vez</b>: ao marcar outra, a anterior é desmarcada. A chave da nota marcada vai para a caixa abaixo da lista, pronta para copiar.
        Também dá para <b>colar a chave de 50 dígitos</b> nessa caixa e usar os botões ao lado.
      </p>

      <h3>Os botões redondos, à direita da lista</h3>
      <p>Passe o mouse sobre cada botão para ver o nome.</p>
      <table className="mn-tabela">
        <thead><tr><th>Botão</th><th>O que faz</th></tr></thead>
        <tbody>
          <tr><td><Botao t="Atualizar Lista" /></td><td>Consulta o ADN e traz as NFS-e novas. Em cada clique ele busca em vários lotes seguidos (até 20) e informa “N NFS-e(s) nova(s) encontrada(s)” ou “Nenhum documento novo encontrado”.</td></tr>
          <tr><td><Botao t="Confirmar Operação" /></td><td>Registra a Confirmação do Tomador na nota marcada. Só funciona em nota pendente.</td></tr>
          <tr><td><Botao t="Rejeitar" /></td><td>Abre, na caixa da chave, o formulário de rejeição (seção 5). Só funciona em nota pendente.</td></tr>
          <tr><td><Botao t="Baixar DANFSe (PDF)" /></td><td>Gera o PDF da nota a partir do XML já guardado.</td></tr>
          <tr><td><Botao t="Baixar XML" /></td><td>Baixa o arquivo XML da nota.</td></tr>
          <tr><td><Botao t="Importar XML pro Lançamento de Notas" /></td><td>Leva a nota para o Lançamento NFS-e 99. Não funciona se a nota já foi lançada ou se foi cancelada pelo prestador.</td></tr>
        </tbody>
      </table>

      <h2 id="dia">4. Passo a passo</h2>
      <Passo n={1} titulo="Atualize a lista">
        <p>Clique em <Botao t="Atualizar Lista" />. A mensagem no alto mostra o resultado.</p>
      </Passo>
      <Passo n={2} titulo="Marque a nota e confira os dados">
        <p>Marque a nota na lista (ou cole a chave na caixa). Use <Botao t="Baixar DANFSe (PDF)" /> para ver a nota completa e conferir o serviço e o valor.</p>
      </Passo>
      <Passo n={3} titulo="Lance a nota">
        <p>Clique em <Botao t="Importar XML pro Lançamento de Notas" />. Ele abre o Lançamento NFS-e 99 já com a nota carregada. Baixar o XML ou o PDF, e confirmar ou rejeitar, continuam possíveis mesmo depois de a nota ser lançada.</p>
      </Passo>
      <Passo n={4} titulo="Confirme ou rejeite">
        <p>Se o serviço foi prestado como a nota diz, clique em <Botao t="Confirmar Operação" />. Se há algum problema, clique em <Botao t="Rejeitar" /> (seção 5).</p>
      </Passo>
      <Atencao>Confirmar e rejeitar são <b>enviados direto ao sistema nacional e não podem ser desfeitos</b>. Confira a nota marcada antes de clicar.</Atencao>

      <h2 id="rejeitar">5. Como rejeitar uma NFS-e</h2>
      <ol>
        <li>Marque a nota pendente e clique em <Botao t="Rejeitar" />. O formulário aparece na caixa da chave.</li>
        <li>Escolha o <b>Motivo da rejeição</b>:
          <ul>
            <li>1 - NFS-e em duplicidade</li>
            <li>2 - NFS-e já emitida pelo tomador</li>
            <li>3 - Não ocorrência do fato gerador</li>
            <li>4 - Erro quanto à responsabilidade tributária</li>
            <li>5 - Erro quanto ao valor do serviço, deduções ou data do fato gerador</li>
            <li>9 - Outros</li>
          </ul>
        </li>
        <li>Escreva a <b>justificativa, de 15 a 255 caracteres</b>. O contador mostra quantos faltam; abaixo de 15 ele fica vermelho.</li>
        <li>Clique em <Botao t="Confirmar Rejeição" />. Para desistir, clique em <Botao t="Cancelar" />.</li>
      </ol>
      <h3>Ver o que já foi registrado na nota</h3>
      <p>Dê um <b>duplo clique na linha</b> para ver os eventos já registrados na nota (confirmação ou rejeição), com data, motivo e justificativa.</p>

      <h2 id="problemas">6. Mensagens e problemas comuns</h2>
      <table className="mn-tabela">
        <thead><tr><th>Mensagem</th><th>O que significa</th><th>O que fazer</th></tr></thead>
        <tbody>
          <tr><td>“Nenhum documento novo encontrado.”</td><td>Não há nada novo além do que o sistema já tem.</td><td>Nada. Tente de novo mais tarde.</td></tr>
          <tr><td>“N NFS-e(s) nova(s) encontrada(s).”</td><td>Foram encontradas N notas de serviço novas.</td><td>Confira a lista.</td></tr>
          <tr><td>“Não foi possível atualizar a lista agora.”</td><td>O sistema nacional não respondeu como esperado.</td><td>Aguarde um pouco e tente de novo. Se persistir, fale com o suporte.</td></tr>
          <tr><td>“Confirmação registrada com sucesso.”</td><td>A confirmação foi aceita. A nota passa a aparecer como Confirmada.</td><td>Nada.</td></tr>
          <tr><td>“Não foi possível registrar a confirmação.”</td><td>O sistema nacional recusou. A mensagem traz o motivo.</td><td>Leia o motivo. Se for algo que você não entende, fale com o suporte.</td></tr>
          <tr><td>“A justificativa precisa ter pelo menos 15 caracteres.”</td><td>O texto da rejeição está curto.</td><td>Explique melhor, com 15 a 255 caracteres.</td></tr>
          <tr><td>“Rejeição registrada com sucesso.”</td><td>A rejeição foi aceita. A nota passa a aparecer como Rejeitada.</td><td>Nada.</td></tr>
          <tr><td>“Certificado digital da empresa não configurado”</td><td>A empresa não tem certificado cadastrado no sistema.</td><td>Peça ao suporte para configurar.</td></tr>
          <tr><td>“Empresa sem CNPJ cadastrado”</td><td>Falta o CNPJ no cadastro da empresa.</td><td>Peça ao suporte para completar o cadastro.</td></tr>
        </tbody>
      </table>
      <p>Os botões <Botao t="Confirmar Operação" /> e <Botao t="Rejeitar" /> ficam desativados em nota já confirmada, já rejeitada ou cancelada pelo prestador. A dica ao passar o mouse diz o motivo.</p>

      <h2 id="duvidas">7. Dúvidas frequentes</h2>
      <h3>A nota existe, mas não aparece na lista.</h3>
      <p>Confira nesta ordem: o <b>período</b> (a tela abre só com os últimos 7 dias); os filtros <b>Situação</b> e <b>Digitada</b>; a <b>empresa</b> do topo; e se você já clicou em <Botao t="Atualizar Lista" />. Para uma nota específica, digite a chave ou o número: o período deixa de valer. Lembre que só aparecem serviços que a sua empresa <b>recebeu</b>.</p>
      <h3>Emiti uma nota de serviço e ela não aparece aqui.</h3>
      <p>É assim mesmo: esta lista tem só as notas em que a empresa é a tomadora do serviço.</p>
      <h3>Confirmei ou rejeitei por engano. Dá para desfazer?</h3>
      <p>Não. A resposta foi enviada ao sistema nacional. Fale com o responsável fiscal para saber como tratar.</p>
      <h3>Posso confirmar várias notas de uma vez?</h3>
      <p>Não. Na NFS-e é uma nota por vez.</p>
      <h3>A nota foi cancelada pelo prestador. E agora?</h3>
      <p>Ela aparece marcada como cancelada e não pode ser lançada nem manifestada. Não é preciso fazer nada nela.</p>
      <h3>Os totais do rodapé não batem com o que eu esperava.</h3>
      <p>Os totais contam só o que está na lista naquele momento. Ajuste o período e os filtros e confira de novo.</p>

      <h2 id="glossario">8. Glossário</h2>
      <table className="mn-tabela">
        <tbody>
          <tr><td><b>NFS-e</b></td><td>Nota Fiscal de Serviço eletrônica.</td></tr>
          <tr><td><b>Tomador</b></td><td>Quem contrata o serviço e recebe a nota. É a sua empresa nesta tela.</td></tr>
          <tr><td><b>Prestador</b></td><td>Quem presta o serviço e emite a nota.</td></tr>
          <tr><td><b>ADN</b></td><td>Ambiente de Dados Nacional, de onde vêm as NFS-e.</td></tr>
          <tr><td><b>DPS</b></td><td>Declaração de Prestação de Serviços: o documento que o prestador envia para gerar a NFS-e.</td></tr>
          <tr><td><b>Chave de acesso</b></td><td>Número único da nota, com 50 dígitos.</td></tr>
          <tr><td><b>Competência</b></td><td>O mês a que o serviço se refere.</td></tr>
          <tr><td><b>Confirmação / Rejeição do tomador</b></td><td>As duas respostas que a empresa pode dar a uma NFS-e recebida.</td></tr>
          <tr><td><b>XML</b></td><td>O arquivo completo da nota, com todos os dados.</td></tr>
          <tr><td><b>DANFSe</b></td><td>A versão em PDF da NFS-e, para ler e imprimir.</td></tr>
          <tr><td><b>Lançada / Digitada</b></td><td>Nota que já foi lançada no sistema (Lançamento de Notas).</td></tr>
        </tbody>
      </table>
    </ManualPagina>
  )
}
